import React, { useEffect, useRef, memo } from "react";
import Button from "components/button";
import Typography from "components/typography";
import Icon, { IconName } from "components/icon";

interface StatusModalProps {
  open: boolean;
  onClose: () => void;
  type: "success" | "error" | "warning";
  title: string;
  message: React.ReactNode;
  testIdPrefix?: string;
  closeLabel?: string;
}

const StatusModal: React.FC<StatusModalProps> = ({
  open,
  onClose,
  type,
  title,
  message,
  testIdPrefix = "status-modal",
  closeLabel = "Close",
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open) {
        dialog.showModal();
        document.body.style.overflow = "hidden";
      }
    } else {
      if (dialog.open) {
        dialog.close();
        document.body.style.overflow = "";
      }
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  const getIconConfig = (): { name: IconName; color: string } => {
    switch (type) {
      case "success":
        return { name: "SealCheck", color: "" };
      case "warning":
        return { name: "WarningCircle", color: "" };
      case "error":
      default:
        return { name: "Warning", color: "" };
    }
  };

  const iconConfig = getIconConfig();

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-white p-0 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop:bg-black/40 max-w-[380px] w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      onClick={handleBackdropClick}
      onClose={onClose}
      data-testid={testIdPrefix}
      aria-labelledby={`${testIdPrefix}-title`}
      aria-describedby={`${testIdPrefix}-message`}
    >
      <div className="flex flex-col w-full">
        {/* Main Content */}
        <div className="flex flex-col items-center pt-10 px-8 pb-8 text-center">
          {/* Icon */}
          <div 
            className={`mb-5 transition-transform duration-500 ${open ? 'scale-110' : 'scale-100'} ${iconConfig.color}`}
            data-testid={`${testIdPrefix}-icon`}
          >
            <Icon name={iconConfig.name} size={64} />
          </div>

          {/* Title */}
          <Typography 
            variant="heading5" 
            className="mb-3 font-semibold text-[#1E293B] text-center"
            id={`${testIdPrefix}-title`}
          >
            {title}
          </Typography>

          {/* Message */}
          <div 
            className="text-[14px] leading-relaxed text-[#64748B] px-2"
            id={`${testIdPrefix}-message`}
            data-testid={`${testIdPrefix}-message`}
          >
            {message}
          </div>
        </div>

        {/* Footer */}
        <div className="w-full px-6 py-5 border-t border-[#E2E8F0] bg-white">
          <Button
            type="button"
            variant="outline"
            color="tertiary"
            size="large"
            isFullSize
            onClick={onClose}
            className="rounded-xl border border-[#2F394A] font-medium text-[#2F394A] bg-white hover:bg-[#F8FAFC] h-[48px] active:scale-[0.98] transition-transform"
            data-testid={`${testIdPrefix}-close-button`}
          >
            {closeLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
};

export default memo(StatusModal);
